<template>
  <InfuseSpinner class="flex items-center justify-center p-16" v-if="isCentralSystemsLoading" size="medium" />

  <div v-else>
    <InfuseNote class="mb-8" v-if="!isCentralSystemsLoading && linkedCentralSystems.length === 0 && !searchQuery">
      {{ t('e_connector.chargingStationLinks.noLinkedCentralSystems') }}
    </InfuseNote>

    <LinkedCentralSystemTable
      :create-enabled="true"
      :editable="true"
      :loading="isCentralSystemsLoading"
      :removable="true"
      :rows="filteredLinkedCentralSystems"
      :search-enabled="true"
      :search-query="searchQuery"
      @create="createModalVisible = true"
      @edit="editRow"
      @remove="(linkedCentralSystem: LinkedCentralSystem) => confirmRemove(linkedCentralSystem)"
      @search="(query: string) => search(query)"
    />
  </div>

  <CreateLinkedCentralSystemModal
    v-if="createModalVisible"
    :loading="isUpserting"
    :central-systems="centralSystems"
    :charging-station="model"
    @close="createModalVisible = false"
    @create="(linkedCentralSystem: LinkedCentralSystem) => create(linkedCentralSystem)"
  />

  <UpdateLinkedCentralSystemModal
    v-if="updateModalVisible && selectedRow"
    :loading="isUpserting"
    :linked-central-system="selectedRow"
    :central-systems="centralSystems"
    :charging-station="model"
    @close="updateModalVisible = false"
    @update="(linkedCentralSystem: LinkedCentralSystem) => update(linkedCentralSystem)"
  />

  <InfuseConfirmModal
    v-if="removeModalVisible"
    :action-title="t('e_connector.chargingStationLinks.modal.remove.title')"
    verification-text="delete"
    @confirm="remove"
    @close="removeModalVisible = false"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { InfuseConfirmModal, InfuseNote, InfuseSpinner } from '@infuse/ui/components';
import {
  CreateLinkedCentralSystemModal,
  LinkedCentralSystemTable,
  UpdateLinkedCentralSystemModal
} from '@infuse/e-connector/charging-station/ui';
import { replaceQueryParams } from '@infuse/ui/utils';
import { LinkedCentralSystem, mapToDomain as mapCentralSystemToDomain } from '@infuse/e-connector/central-system/domain';
import { ChargingStation } from '@infuse/e-connector/charging-station/domain';
import { hydrateLinkedCentralSystems, LinkedCentralSystemHydrated } from '@infuse/e-connector/shared/feature';
import { createEconnectorClient } from '@infuse/api/common';
import { CentralSystem as ApiCentralSystem } from '@infuse/api/e-connector/central-system';
import { upsertCentralSystemLinkDirect } from './util';

const { t } = useI18n();

const route = useRoute();
const router = useRouter();

const allCentralSystems = ref<ApiCentralSystem[]>([]);
const isCentralSystemsLoading = ref(false);
const isUpserting = ref(false);
const centralSystems = computed(() => allCentralSystems.value.map(mapCentralSystemToDomain));

const client = createEconnectorClient('/api');

const fetchCentralSystems = async () => {
  isCentralSystemsLoading.value = true;
  try {
    const response = await client.get<{ content?: ApiCentralSystem[] } | ApiCentralSystem[]>('centralsystems');
    allCentralSystems.value = Array.isArray(response.data) ? response.data : (response.data?.content ?? []);
  } catch (e) {
    console.error('fetchCentralSystems failed:', e);
  } finally {
    isCentralSystemsLoading.value = false;
  }
};

const fetchStations = async () => {
  emit('reload-model');
};

const searchQuery = ref<string | undefined>(route.query.search?.toString());

const createModalVisible = ref<boolean>(false);
const updateModalVisible = ref<boolean>(false);
const removeModalVisible = ref<boolean>(false);
const selectedRow = ref<LinkedCentralSystem>();

const linkedCentralSystems = ref<LinkedCentralSystemHydrated[]>([]);
const filteredLinkedCentralSystems = ref<LinkedCentralSystemHydrated[]>([]);

interface Emits {
  (e: 'reload-model'): void;
}
const emit = defineEmits<Emits>();

interface Props {
  model: ChargingStation;
}
const props = withDefaults(defineProps<Props>(), {});

onMounted(async () => {
  await fetchCentralSystems();
});

watch(() => props.model, rehydrateLinkedCentralSystems);
watch(centralSystems, rehydrateLinkedCentralSystems); // rehydrate on central systems load

async function search(search: string) {
  // local search since we use the LinkedCentralSystem model, not the paginated central systems directly
  searchQuery.value = search;
  replaceQueryParams(router, 'charging-stations.linkedSystems', { search: searchQuery.value });

  if (search) {
    filteredLinkedCentralSystems.value = linkedCentralSystems.value.filter((linkedCentralSystem) =>
      [
        ...Object.values(linkedCentralSystem),
        ...Object.values(linkedCentralSystem.chargingStation),
        ...Object.values(linkedCentralSystem.centralSystem)
      ].some((v) => v?.toString().toLowerCase().includes(search.toLowerCase()))
    );
  } else {
    filteredLinkedCentralSystems.value = [...linkedCentralSystems.value];
  }
}

async function create(linkedCentralSystem: LinkedCentralSystem) {
  await upsertCentralSystemLinkDirect(linkedCentralSystem, isUpserting, allCentralSystems, client);
  createModalVisible.value = false;
  await fetchStations();
}

async function update(linkedCentralSystem: LinkedCentralSystem) {
  await upsertCentralSystemLinkDirect(linkedCentralSystem, isUpserting, allCentralSystems, client);
  updateModalVisible.value = false;
  await fetchStations();
}

function editRow(row: LinkedCentralSystem) {
  selectedRow.value = row;
  updateModalVisible.value = true;
}

function confirmRemove(row: LinkedCentralSystem) {
  selectedRow.value = row;
  removeModalVisible.value = true;
}

async function remove() {
  if (!selectedRow.value) {
    return;
  }

  await upsertCentralSystemLinkDirect(selectedRow.value, isUpserting, allCentralSystems, client, true);

  removeModalVisible.value = false;
  selectedRow.value = undefined;
  await fetchStations();
}

function rehydrateLinkedCentralSystems() {
  if (props.model) {
    linkedCentralSystems.value = hydrateLinkedCentralSystems(props.model, centralSystems.value) ?? [];
    filteredLinkedCentralSystems.value = [...linkedCentralSystems.value];
  } else {
    console.warn('No charging station provided?');
  }
}
</script>
